import React, { useState, useEffect } from 'react';
import { Tabs, Card, Alert } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  LockOutlined,
  IdcardOutlined,
  TeamOutlined,
  EditOutlined,
  TrophyOutlined,
  AppstoreOutlined,
  LinkOutlined
} from '@ant-design/icons';
import MyDashboard from './MyDashboard';
import General from './General';
import UserInformation from './UserInformation';
import * as actions from 'store/actions/AuthActions';
import Leaves from './Leaves';
import HR from './HR';
import Payslips from './Payslips';
import DailyUpdates from './DailyUpdates';
import PerformanceReview from './PerformanceReview';
import ImportantLinks from './ImportantLinks';
import './MyProfile.css';
import useUrlFilters from 'hooks/useUrlFilters';


// Active tab lives in the URL so links to a tab's filtered view open on that tab.
const URL_FILTERS = {
  tab: { type: 'string', default: '0' },
};

const MyProfile = () => {
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const activeTab = urlFilters.tab;
  const setActiveTab = (value) => setUrlFilters({ tab: value }, { replace: false });
  const [employee, setEmployee] = useState(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        const response = await actions.getCurrentUserEmployee();
        if (response && !response.error) {
          setEmployee(response);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  const isProfileLocked = employee?.profile_locked || false;

  const tabItems = [
    {
      key: '0',
      label: (
        <span>
          <AppstoreOutlined style={{ marginRight: '8px' }} />
          Dashboard
        </span>
      ),
      children: <MyDashboard employee={employee} />,
    },
    {
      key: '1',
      label: (
        <span>
          <IdcardOutlined style={{ marginRight: '8px' }} />
          General
        </span>
      ),
      children: <General isProfileLocked={isProfileLocked} employee={employee} />,
    },
    {
      key: '2',
      label: (
        <span>
          <UserOutlined style={{ marginRight: '8px' }} />
          Profile
        </span>
      ),
      children: <UserInformation isProfileLocked={isProfileLocked} employee={employee} />,
    },
    {
      key: '3',
      label: (
        <span>
          <LinkOutlined style={{ marginRight: '8px' }} />
          Important Links
        </span>
      ),
      children: <ImportantLinks employee={employee} />,
    },
    // {
    //   key: '3a',
    //   label: (
    //     <span>
    //       <DollarOutlined style={{ marginRight: '8px' }} />
    //       Salary Info
    //     </span>
    //   ),
    //   children: <SalaryInfo isProfileLocked={isProfileLocked} />,
    // },
    {
      key: '4',
      label: (
        <span>
          <CalendarOutlined style={{ marginRight: '8px' }} />
          Timesheet
        </span>
      ),
      children: <Leaves isProfileLocked={isProfileLocked} employee={employee} />,
    },
    {
      key: '5',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: '8px' }} />
          HR
        </span>
      ),
      children: <HR isProfileLocked={isProfileLocked} employee={employee} />,
    },
    {
      key: '6',
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: '8px' }} />
          Payslip
        </span>
      ),
      children: <Payslips isProfileLocked={isProfileLocked} />,
    },
    {
      key: '7',
      label: (
        <span>
          <EditOutlined style={{ marginRight: '8px' }} />
          Daily Updates
        </span>
      ),
      children: <DailyUpdates isProfileLocked={false} employee={employee} />,
    },
    {
      key: '8',
      label: (
        <span>
          <TrophyOutlined style={{ marginRight: '8px' }} />
          Performance
        </span>
      ),
      children: <PerformanceReview isProfileLocked={isProfileLocked} employee={employee} />,
    },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
      {employee?.is_verified === false && (
        <Alert
          message={<span style={{ fontWeight: 500 }}>Verification Pending: {employee?.unverified_reason || 'Profile is not complete'}</span>}
          type="warning"
          showIcon
          banner
          style={{ marginBottom: 8, flexShrink: 0 }}
        />
      )}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isProfileLocked && <LockOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />}
            My Profile
            {isProfileLocked && <span style={{ color: '#ff4d4f', marginLeft: '8px', fontSize: '14px' }}>(Locked)</span>}
          </div>
        }
        style={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        bodyStyle={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0
        }}
      >
        {isProfileLocked && (
          <Alert
            message="Profile Locked"
            description="Your profile has been locked by an administrator. You can view your information but cannot make changes."
            type="warning"
            showIcon
            style={{ margin: '16px 24px 0 24px' }}
          />
        )}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="line"
          size="large"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
          tabBarStyle={{
            margin: 0,
            paddingLeft: '24px',
            paddingRight: '24px',
            flexShrink: 0
          }}
          className="myprofile-tabs"
        />
      </Card>
    </div>
  );
};

export default MyProfile;